import React, { useRef, useState } from 'react';
import { Star } from 'lucide-react';

/*
 * standard_2026 — reusable respondent presentation primitives.
 * Shared + brand-agnostic. Activated only when a survey resolves to
 * presentation_version === "standard_2026" (legacy_v1 never imports these).
 * Branding (logo / primary / secondary / wording) is layered by the caller.
 */

// Approved black-and-white NPS face progression (0..10): line-art faces —
// 0–6 frown, 7–8 neutral, 9–10 smile (Detractor / Passive / Promoter bands).
// Inline SVG so they stay crisp at any size. No colour, no emoji, no slider.
const npsFace = (v) => (v <= 6 ? 'frown' : v <= 8 ? 'neutral' : 'smile');
const NPS_MOUTH = {
  frown: 'M7.8 17.1 Q12 13.4 16.2 17.1',
  neutral: 'M8.2 15.6 H15.8',
  smile: 'M7.8 14.2 Q12 18.2 16.2 14.2',
};

function NpsFaceIcon({ score }) {
  return (
    <svg viewBox="0 0 24 24" className="std2026-nps-svg" aria-hidden="true" data-face={npsFace(score)}>
      <circle cx="12" cy="12" r="10.3" fill="#fff" stroke="#111827" strokeWidth="1.5" />
      <circle cx="8.7" cy="9.5" r="1.2" fill="#111827" />
      <circle cx="15.3" cy="9.5" r="1.2" fill="#111827" />
      <path d={NPS_MOUTH[npsFace(score)]} fill="none" stroke="#111827" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

// Approved 5-score colour semantics for stars (exact values).
export const STAR_COLORS = {
  1: '#C63734', // red
  2: '#E09FB1', // pink
  3: '#8B929C', // grey
  4: '#9CC58E', // light green
  5: '#326941', // dark green
};
const STAR_EMPTY = '#D0D5DB';

// Touch/pen "press" state for the NPS row (touch has no hover). The pressed
// face follows a horizontal slide; lifting selects it; vertical drags become
// page scroll (touch-action: pan-y → pointercancel). Mouse keeps CSS :hover.
function useNpsPress(onChange) {
  const rowRef = useRef(null);
  const pressRef = useRef(null);
  const ignoreClickUntil = useRef(0);
  const [pressed, setPressedState] = useState(null);
  const setPressed = (v) => { pressRef.current = v; setPressedState(v); };
  const touchLike = (e) => e.pointerType === 'touch' || e.pointerType === 'pen';
  const valueAt = (x, y) => {
    const r = rowRef.current.getBoundingClientRect();
    if (y < r.top - 48 || y > r.bottom + 48) return null;
    return Math.min(10, Math.max(0, Math.floor(((x - r.left) / r.width) * 11)));
  };
  const handlers = {
    onPointerDown: (e) => {
      const el = touchLike(e) && e.target.closest('[data-nps-value]');
      if (el) setPressed(Number(el.getAttribute('data-nps-value')));
    },
    onPointerMove: (e) => {
      if (!touchLike(e) || pressRef.current === null) return;
      const v = valueAt(e.clientX, e.clientY);
      if (v !== pressRef.current) setPressed(v);
    },
    onPointerUp: (e) => {
      if (!touchLike(e)) return;
      const v = pressRef.current;
      setPressed(null);
      ignoreClickUntil.current = Date.now() + 700;
      if (v !== null) onChange(v);
    },
    onPointerCancel: () => setPressed(null),
  };
  const onClick = (v) => { if (Date.now() >= ignoreClickUntil.current) onChange(v); };
  return { rowRef, pressed, handlers, onClick };
}

// NPS 0–10, black-and-white faces + numbers, endpoint labels only.
export function NpsFaces2026({ question, answer, onChange }) {
  const values = Array.from({ length: 11 }, (_, i) => i);
  const hasAnswer = typeof answer === 'number';
  const { rowRef, pressed, handlers, onClick } = useNpsPress(onChange);
  return (
    <div className="std2026-nps" data-testid={`nps-scale-${question.id}`} data-nps-appearance="bw">
      <div className="std2026-nps-row" ref={rowRef} {...handlers} data-testid={`nps-row-${question.id}`}>
        {values.map((v) => {
          const selected = answer === v;
          return (
            <button
              key={v}
              type="button"
              className={`std2026-nps-opt${selected ? ' is-selected' : ''}${pressed === v ? ' is-pressed' : ''}`}
              onClick={() => onClick(v)}
              data-nps-value={v}
              data-pressed={pressed === v ? 'true' : 'false'}
              data-testid={`nps-score-${v}`}
              data-selected={selected ? 'true' : 'false'}
              aria-label={`Score ${v}`}
              aria-pressed={selected}
            >
              <span className="std2026-nps-face">
                <NpsFaceIcon score={v} />
              </span>
              <span className="std2026-nps-num">{v}</span>
            </button>
          );
        })}
      </div>
      <div className="std2026-nps-labels">
        <span data-testid="nps-label-0">Not at all likely</span>
        <span data-testid="nps-label-10">Extremely likely</span>
      </div>
      {hasAnswer && (
        <div className="std2026-nps-chip" data-testid={`nps-selected-chip-${question.id}`}>
          You selected <strong>{answer}</strong>
        </div>
      )}
    </div>
  );
}

// Attribute star rating — fill colour follows the SELECTED score (1..5).
// Grouped attribute ratings — all attributes on ONE screen/step. Reusable:
// attributes come from question config; each answer is stored separately under
// its attribute id so reporting can aggregate per attribute.
export function AttributeRatings2026({ question, answer, onChange }) {
  const attributes = Array.isArray(question.attributes) ? question.attributes : [];
  const value = (answer && typeof answer === 'object') ? answer : {};
  const setAttr = (id, score) => onChange({ ...value, [id]: score });
  return (
    <div className="std2026-attrs" data-testid={`attr-group-${question.id}`}>
      {question.attr_instruction && (
        <p className="std2026-attr-instruction">{question.attr_instruction}</p>
      )}
      <div className="std2026-attr-list">
        {attributes.map((a) => (
          <div className="std2026-attr-row" key={a.id} data-testid={`attr-row-${a.id}`}>
            <span className="std2026-attr-label">{a.label}</span>
            <StarRating2026
              question={{ id: `${question.id}-${a.id}`, star_settings: { max_stars: 5 } }}
              answer={value[a.id]}
              onChange={(v) => setAttr(a.id, v)}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

// Feedback-code access screen shell (VISUAL ONLY — no validation/parsing; the
// reusable validator is Phase 3). Supports QR note + manual code entry.
export function AccessCode2026({ question, answer, onChange, primaryColor = '#1A3E6F', showQrHint = true }) {
  return (
    <div className="std2026-access" data-testid={`access-${question.id}`}>
      {showQrHint && (
        <div className="std2026-access-qr" aria-hidden="true">
          <span className="std2026-access-qricon" style={{ borderColor: primaryColor }}>QR</span>
          <p className="std2026-qhelp">Scan the QR code on your receipt, or enter your feedback code below.</p>
        </div>
      )}
      <label className="std2026-access-label">{question.access_label || 'Feedback code'}</label>
      <input
        type="text"
        className="std2026-access-input"
        value={answer || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={question.access_placeholder || 'e.g. 035261026221576'}
        data-testid={`access-input-${question.id}`}
        inputMode="text"
        autoComplete="off"
      />
      <p className="std2026-access-note">Validation is applied after you continue.</p>
    </div>
  );
}

export function StarRating2026({ question, answer, onChange }) {
  const max = Math.min(5, Math.max(1, question.star_settings?.max_stars ?? 5));
  const selected = Number(answer) || 0;
  const fill = STAR_COLORS[selected] || STAR_EMPTY;
  return (
    <div className="std2026-stars" data-testid={`stars-${question.id}`}>
      {Array.from({ length: max }).map((_, i) => {
        const val = i + 1;
        const on = selected >= val;
        return (
          <button
            key={i}
            type="button"
            className="std2026-star-btn"
            onClick={() => onChange(val)}
            data-testid={`star-${question.id}-${val}`}
            data-active={on ? 'true' : 'false'}
            aria-label={`${val} star${val === 1 ? '' : 's'}`}
            aria-pressed={on}
          >
            <Star
              className="std2026-star"
              strokeWidth={1.5}
              fill={on ? fill : STAR_EMPTY}
              stroke={on ? fill : STAR_EMPTY}
            />
          </button>
        );
      })}
    </div>
  );
}
